import React, { useState, useEffect } from 'react';
import { toNumberId } from '../utils/typeUtils';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tabs,
  Tab
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { waitlistApi, resourcesApi, usersApi } from '../services/api';
import { WaitlistEntry, Resource, User } from '../types';
import WaitlistList from '../components/waitlist/WaitlistList';
import { useAuth } from '../auth/AuthContext';
import { format, addDays } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`waitlist-tabpanel-${index}`}
      aria-labelledby={`waitlist-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminWaitlist: React.FC = () => {
  const { user } = useAuth();
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Filters
  const [selectedResource, setSelectedResource] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const fetchWaitlistEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Format dates for API query if they exist
      const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
      const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;
      
      let entries: WaitlistEntry[] = [];
      
      if (selectedResource && selectedResource !== '') {
        // Get entries by resource
        entries = await waitlistApi.getByResource(
          parseInt(selectedResource, 10), 
          formattedStartDate, 
          formattedEndDate
        );
      } else if (selectedUser && selectedUser !== '') {
        // Get entries by user
        const userIdNum = toNumberId(selectedUser);
        if (userIdNum) {
          entries = await waitlistApi.getByUser(userIdNum);
        } else {
          console.error('Invalid user ID format:', selectedUser);
          setError('Invalid user ID format');
          return;
        }
      } else {
        // Get all entries
        entries = await waitlistApi.getAll(formattedStartDate, formattedEndDate);
      }
      
      // Fetch related resources and users for display
      const resourceIdsSet = new Set(entries.map(entry => entry.resourceId));
      const userIdsSet = new Set(entries.map(entry => entry.userId));
      
      const resourceIds = Array.from(resourceIdsSet);
      const userIds = Array.from(userIdsSet);
      
      const resourcesData = await Promise.all(
        resourceIds.map(id => resourcesApi.getById(id))
      );
      
      const usersData = await Promise.all(
        userIds.map(id => usersApi.getById(id))
      );
      
      // Attach resource and user objects to entries
      const enrichedEntries = entries.map(entry => ({
        ...entry,
        resource: resourcesData.find(r => r.id === entry.resourceId),
        user: usersData.find(u => u.id === entry.userId)
      }));
      
      setWaitlistEntries(enrichedEntries);
    } catch (err: any) {
      console.error('Error fetching waitlist entries:', err);
      setError(err.response?.data?.error || 'Failed to load waitlist entries. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFiltersData = async () => {
    try {
      const [resourcesData, usersData] = await Promise.all([
        resourcesApi.getAll(),
        usersApi.getAll()
      ]);
      
      setResources(resourcesData.filter(r => r.isActive));
      setUsers(usersData);
    } catch (err: any) {
      console.error('Error fetching filters data:', err);
    }
  };

  useEffect(() => {
    fetchFiltersData();
    fetchWaitlistEntries();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Reset filters when changing tabs
    setSelectedResource('');
    setSelectedUser('');
    setStartDate(null);
    setEndDate(null);
    
    fetchWaitlistEntries();
  };

  const handleResourceChange = (event: SelectChangeEvent<string>) => {
    setSelectedResource(event.target.value);
    setSelectedUser(''); // Reset user filter when resource is selected
  };

  const handleUserChange = (event: SelectChangeEvent<string>) => {
    setSelectedUser(event.target.value);
    setSelectedResource(''); // Reset resource filter when user is selected
  };

  const handleApplyFilters = () => {
    fetchWaitlistEntries();
  };

  const handleResetFilters = () => {
    setSelectedResource('');
    setSelectedUser('');
    setStartDate(null);
    setEndDate(null);
    fetchWaitlistEntries();
  };

  // Filter entries based on tab
  const pendingEntries = waitlistEntries.filter(entry => 
    entry.status === 'pending' || entry.status === 'notified'
  );
  
  const completedEntries = waitlistEntries.filter(entry => 
    entry.status === 'converted' || entry.status === 'cancelled'
  );

  if (!user || user.role !== 'admin') {
    return (
      <Box>
        <Alert severity="error">
          You don't have permission to access this page. Admin access required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>Waitlist Management</Typography>
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ width: { xs: '100%', sm: '47%', md: '23%' } }}>
            <FormControl fullWidth>
              <InputLabel id="resource-select-label">Resource</InputLabel>
              <Select
                labelId="resource-select-label"
                value={selectedResource}
                label="Resource"
                onChange={handleResourceChange}
              >
                <MenuItem value="">
                  <em>All Resources</em>
                </MenuItem>
                {resources.map((resource) => (
                  <MenuItem key={resource.id} value={resource.id}>
                    {resource.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ width: { xs: '100%', sm: '47%', md: '23%' } }}>
            <FormControl fullWidth>
              <InputLabel id="user-select-label">User</InputLabel>
              <Select
                labelId="user-select-label"
                value={selectedUser}
                label="User"
                onChange={handleUserChange}
              >
                <MenuItem value="">
                  <em>All Users</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ width: { xs: '100%', sm: '47%', md: '15%' } }}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', sm: '47%', md: '15%' } }}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', md: '15%' }, display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              onClick={handleApplyFilters}
              fullWidth
            >
              Apply
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleResetFilters}
              fullWidth
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="waitlist tabs">
          <Tab label="Active Waitlist" id="waitlist-tab-0" />
          <Tab label="Completed Entries" id="waitlist-tab-1" />
        </Tabs>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            <WaitlistList 
              entries={pendingEntries} 
              onUpdate={fetchWaitlistEntries}
              showResource={true}
              showUser={true}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <WaitlistList 
              entries={completedEntries} 
              onUpdate={fetchWaitlistEntries}
              showResource={true}
              showUser={true}
            />
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default AdminWaitlist;
